import React, {useState, useEffect} from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Container from "connectui/component/layout/Container";
import SvgIcon from "connectui/component/display/SvgIcon";
import ListComponent from "connectui/component/layout/list/ListComponent";
import ListItemContainer from "connectui/component/layout/list/ListItemContainer";
import CheckBox from "connectui/component/input/CheckBox";
import Text from "connectui/component/display/Text";
import TextInput from "connectui/component/input/text/type/TextInput";
import OutlinedInputBase from "connectui/component/input/text/OutlinedInputBase";
import Tooltip from "connectui/component/display/Tooltip";
import ICONS from "connectui/icons/iconMap";
import {useTranslation} from "react-i18next";
import {fieldParams} from "../../../../configuration/specs";
import _ from "lodash";

const useStyles = (theme) =>
  buildStyles({
    menuContainer: {
      padding: theme.spacing(2, 2),
      minWidth: "250px",
      maxHeight: "400px",
    },
    searchIcon: {
      width: "15px",
      height: "15px",
      color: theme.colors.textPrimary,
      marginRight: theme.spacing(2),
    },
    xIcon: {
      width: "11px",
      height: "11px",
      cursor: "pointer",
      color: theme.colors.gray500,
    },
    listContainer: {
      maxHeight: "300px",
      overflowY: "auto",
    },
  });

const CrmColumnsMenu = ({
  allColumns,
  selectedColumns,
  setSelectedColumns,
  columnsMenuAnchorEl,
  closeColumnsMenu,
  customFields = {},
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedColumns, setTempSelectedColumns] = useState([]);
  const {t} = useTranslation();

  useEffect(() => {
    if (columnsMenuAnchorEl) {
      setTempSelectedColumns([...selectedColumns]);
    }
  }, [columnsMenuAnchorEl, selectedColumns]);

  const getFieldLabel = (field) => {
    const customFieldsMap = new Map();
    if (customFields && Object.keys(customFields).length > 0) {
      Object.values(customFields)
        .flat()
        .forEach((customField) => {
          if (customField.field && customField.name) {
            customFieldsMap.set(customField.field, customField.name);
          }
        });
    }

    return fieldParams[field]?.name || customFieldsMap.get(field) || field;
  };

  const filteredColumns = allColumns.filter((field) => {
    const fieldName = getFieldLabel(field);
    return fieldName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleColumnToggle = (field) => {
    setTempSelectedColumns((prevColumns) =>
      prevColumns.includes(field)
        ? prevColumns.filter((f) => f !== field)
        : [...prevColumns, field],
    );
  };

  const handleClose = () => {
    if (!_.isEqual(tempSelectedColumns, selectedColumns)) {
      setSelectedColumns(tempSelectedColumns);
    }
    setSearchQuery("");
    closeColumnsMenu();
  };

  return (
    <ContextMenu
      open={Boolean(columnsMenuAnchorEl)}
      onClose={handleClose}
      anchorEl={columnsMenuAnchorEl}
      customContent={
        <Container className={classes.menuContainer}>
          <TextInput
            inputBase={OutlinedInputBase}
            startIcon={
              <SvgIcon
                component={ICONS.search}
                className={classes.searchIcon}
                sx={{color: theme.colors.gray500}}
              />
            }
            sx={{
              padding: theme.spacing(1, 3.5),
              marginBottom: theme.spacing(2),
            }}
            inputProps={{
              placeholder: t(
                "crm-guest-table-columns-search",
                "Search columns...",
              ),
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value),
            }}
            actions={
              !!searchQuery && (
                <SvgIcon
                  component={ICONS.x}
                  className={classes.xIcon}
                  onClick={() => setSearchQuery("")}
                />
              )
            }
          />

          <ListComponent className={classes.listContainer} gap={"0px"}>
            {filteredColumns.map((field) => {
              const isChecked = tempSelectedColumns.includes(field);
              return (
                <ListItemContainer
                  key={field}
                  onClick={() => handleColumnToggle(field)}
                  itemProps={{
                    sx: {
                      backgroundColor: isChecked
                        ? theme.colors.gray50
                        : "transparent",
                      borderRadius: 0,
                      padding: theme.spacing(1.5, 2),
                    },
                  }}
                >
                  <Tooltip
                    tooltipContent={getFieldLabel(field)}
                    sx={{
                      tooltip: {
                        maxWidth: "300px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      },
                      trigger: {
                        display: "block",
                        width: "100%",
                        maxWidth: "300px",
                      },
                    }}
                  >
                    <Text
                      as="div"
                      variant="textSM"
                      weight="medium"
                      color="gray700"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "300px",
                      }}
                    >
                      {getFieldLabel(field)}
                    </Text>
                  </Tooltip>
                  <FlexContainer alignItems="center" sx={{height: "100%"}}>
                    <CheckBox
                      isChecked={isChecked}
                      onChange={() => handleColumnToggle(field)}
                    />
                  </FlexContainer>
                </ListItemContainer>
              );
            })}
          </ListComponent>
        </Container>
      }
    />
  );
};

export default CrmColumnsMenu;
